/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Mortgage pipeline — kanban by stage and list.
 * GET /re/mortgage-applications (stage, lender_id, assigned_to, search),
 * POST /re/mortgage-applications/{id}/stage {to_stage}, POST /re/mortgage-applications.
 * Cards move by drag and drop or the "Move to" menu.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Icon, IconButton, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { usePermission } from 'auth/usePermission';
import useDebounce from 'hooks/useDebounce';
import RePage, { FilterBar, LoadError, RePanel, ReSearch, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { RePicker, UserPicker } from './components/Pickers';
import { Num } from './components/Widgets';
import MortgageForm from './components/MortgageForm';
import { TONES, toneFor } from './components/statusColors';
import {
  MORTGAGE_STAGES, formatMoney, formatPct, humanize, toNumber, useReApi, useReList,
} from './api';
import { defaultCurrency, isAustralia } from '../variant';
import { useIsReV2 } from '../v2/parts';
import SegmentedControl from 'components/v2/SegmentedControl';
import { Kanban, List } from 'lucide-react';

const VIEW_OPTIONS = [{ value: 'board', label: 'Board', icon: Kanban }, { value: 'list', label: 'List', icon: List }];

const Badge = ({ label, value, warn }) => (
  <MDBox
    component="span"
    sx={{
      px: 0.75, py: 0.1, borderRadius: '4px', fontSize: '0.68rem', fontWeight: 700,
      bgcolor: warn ? TONES.error.background : TONES.neutral.background,
      color: warn ? TONES.error.text : TONES.neutral.text,
    }}
  >
    {`${label} ${value}`}
  </MDBox>
);

function AppCard({ a, onOpen, onMove, canEdit }) {
  const [anchor, setAnchor] = useState(null);
  const dbr = toNumber(a.dbr_pct);
  const ltv = toNumber(a.ltv_pct);
  return (
    <Card
      draggable={canEdit}
      onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(a.id)); e.dataTransfer.effectAllowed = 'move'; }}
      onClick={() => onOpen(a)}
      sx={{ p: 1.25, mb: 1, borderRadius: '8px', border: '1px solid #E8EDF5', boxShadow: 'none', cursor: 'pointer', '&:hover': { borderColor: '#1565C8' } }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
        <MDBox minWidth={0}>
          <MDTypography variant="button" fontWeight="bold" display="block" noWrap>{a.applicant_name || a.applicant_contact_name || `#${a.id}`}</MDTypography>
          <MDTypography variant="caption" color="text" display="block" noWrap>{[a.application_number, a.lender_name].filter(Boolean).join(' · ') || '—'}</MDTypography>
        </MDBox>
        {canEdit && (
          <>
            <Tooltip title="Move to">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}><Icon fontSize="small">more_vert</Icon></IconButton>
            </Tooltip>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}>
              {MORTGAGE_STAGES.filter((s) => s !== a.stage).map((s) => (
                <MenuItem key={s} onClick={() => { setAnchor(null); onMove(a, s); }}>{`Move to ${humanize(s)}`}</MenuItem>
              ))}
            </Menu>
          </>
        )}
      </MDBox>
      <MDBox mt={0.75}>
        <Num bold>{formatMoney(a.loan_amount, a.currency, { compact: true })}</Num>
      </MDBox>
      <MDBox display="flex" gap={0.5} mt={0.75} flexWrap="wrap">
        {ltv != null && <Badge label={isAustralia() ? 'LVR' : 'LTV'} value={formatPct(ltv, 0)} warn={isAustralia() && ltv > 80} />}
        {dbr != null && !isAustralia() && <Badge label="DBR" value={formatPct(dbr, 0)} warn={dbr > 50} />}
      </MDBox>
    </Card>
  );
}

export default function MortgagePipeline() {
  const navigate = useNavigate();
  const { api } = useReApi();
  const [snack, notify] = useSnack();
  const { canCreate, canEdit } = usePermission('re_mortgage_applications');
  const [view, setView] = useState('board');
  const isV2 = useIsReV2();
  const [lenderId, setLenderId] = useState(null);
  const [assigned, setAssigned] = useState(null);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 350);
  const [newOpen, setNewOpen] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const filters = { lender_id: lenderId, assigned_to: assigned, search: debounced };
  const board = useReList(view === 'board' ? '/re/mortgage-applications' : null, filters, { pageSize: 200 });
  const list = useReList(view === 'list' ? '/re/mortgage-applications' : null, filters);

  const byStage = useMemo(() => {
    const g = {};
    MORTGAGE_STAGES.forEach((s) => { g[s] = []; });
    board.items.forEach((a) => { (g[a.stage] || (g[a.stage] = [])).push(a); });
    return g;
  }, [board.items]);

  const move = async (a, toStage) => {
    if (a.stage === toStage) return;
    board.setItems((items) => items.map((x) => (x.id === a.id ? { ...x, stage: toStage } : x)));
    try {
      await api.post(`/re/mortgage-applications/${a.id}/stage`, { to_stage: toStage });
      notify(`Moved to ${humanize(toStage)}`);
    } catch (e) {
      notify.error(e, 'Could not change the stage');
    } finally {
      board.reload();
      list.reload();
    }
  };

  const onDrop = (stage) => (e) => {
    e.preventDefault();
    setDragOver(null);
    const idStr = e.dataTransfer.getData('text/plain');
    const a = board.items.find((x) => String(x.id) === idStr);
    if (a) move(a, stage);
  };

  const open = (a) => navigate(`/real-estate/mortgage-applications/${a.id}`);

  return (
    <RePage
      title="Mortgage pipeline"
      subtitle="Applications from enquiry to disbursal"
      actions={(
        <>
          {isV2 ? (
            <SegmentedControl label="View" options={VIEW_OPTIONS} value={view} onChange={setView} />
          ) : (
            <ToggleButtonGroup exclusive size="small" value={view} onChange={(e, v) => v && setView(v)}>
              <ToggleButton value="board"><Icon fontSize="small">view_kanban</Icon>&nbsp;Board</ToggleButton>
              <ToggleButton value="list"><Icon fontSize="small">list</Icon>&nbsp;List</ToggleButton>
            </ToggleButtonGroup>
          )}
          {canCreate && (
            <MDButton variant="gradient" color="info" size="small" onClick={() => setNewOpen(true)}>
              <Icon>add</Icon>&nbsp;New application
            </MDButton>
          )}
        </>
      )}
    >
      <FilterBar>
        <ReSearch label="Search applicant / ref" value={search} onChange={setSearch} />
        <RePicker path="/re/lenders" label="Lender" value={lenderId} onChange={setLenderId} />
        <UserPicker label="Advisor" value={assigned} onChange={setAssigned} />
      </FilterBar>

      {view === 'board' ? (
        <>
          <LoadError error={board.error} onRetry={board.reload} />
          <MDBox display="flex" gap={1.5} sx={{ overflowX: 'auto', pb: 2 }}>
            {MORTGAGE_STAGES.map((stage) => {
              const t = TONES[toneFor('mortgage', stage)];
              const items = byStage[stage] || [];
              const total = items.reduce((s, a) => s + (toNumber(a.loan_amount) || 0), 0);
              return (
                <MDBox
                  key={stage}
                  onDragOver={(e) => { if (canEdit) { e.preventDefault(); setDragOver(stage); } }}
                  onDragLeave={() => setDragOver((d) => (d === stage ? null : d))}
                  onDrop={onDrop(stage)}
                  sx={{
                    minWidth: 240, width: 240, flexShrink: 0, p: 1, borderRadius: '10px',
                    bgcolor: dragOver === stage ? '#E8F0FE' : '#F4F6F9',
                    border: `1px dashed ${dragOver === stage ? '#1565C8' : 'transparent'}`,
                  }}
                >
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1} px={0.5} sx={{ borderTop: `3px solid ${t.dot}`, pt: 0.75 }}>
                    <MDTypography variant="button" fontWeight="bold">{humanize(stage)}</MDTypography>
                    <MDTypography variant="caption" color="text">{items.length}</MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text" display="block" px={0.5} mb={1}>{formatMoney(total, defaultCurrency(), { compact: true })}</MDTypography>
                  {board.loading && !board.items.length ? null : items.map((a) => (
                    <AppCard key={a.id} a={a} onOpen={open} onMove={move} canEdit={canEdit} />
                  ))}
                </MDBox>
              );
            })}
          </MDBox>
          {board.total > board.items.length && (
            <MDTypography variant="caption" color="text">{`Showing ${board.items.length} of ${board.total}. Use the filters or the list view to see the rest.`}</MDTypography>
          )}
        </>
      ) : (
        <>
          <LoadError error={list.error} onRetry={list.reload} />
          <RePanel p={0}>
            <ReTable
              loading={list.loading}
              rows={list.items}
              emptyMessage="No applications."
              onRowClick={open}
              total={list.total}
              page={list.page}
              pageSize={list.pageSize}
              onPageChange={list.setPage}
              onPageSizeChange={list.setPageSize}
              columns={[
                { key: 'application_number', label: 'Ref', render: (a) => <strong>{a.application_number || `#${a.id}`}</strong> },
                { key: 'applicant', label: 'Applicant', render: (a) => a.applicant_name || a.applicant_contact_name || '—' },
                { key: 'lender_name', label: 'Lender', render: (a) => a.lender_name || '—' },
                { key: 'loan_amount', label: 'Loan', align: 'right', render: (a) => <Num>{formatMoney(a.loan_amount, a.currency, { decimals: 0 })}</Num> },
                { key: 'ltv_pct', label: isAustralia() ? 'LVR' : 'LTV', align: 'right', render: (a) => formatPct(a.ltv_pct) },
                ...(isAustralia() ? [] : [
                  { key: 'dbr_pct', label: 'DBR', align: 'right', render: (a) => <Num bold={toNumber(a.dbr_pct) > 50} color={toNumber(a.dbr_pct) > 50 ? '#BA1A1A' : null}>{formatPct(a.dbr_pct)}</Num> },
                ]),
                { key: 'assigned_to_name', label: 'Advisor', render: (a) => a.assigned_to_name || '—' },
                { key: 'stage', label: 'Stage', render: (a) => <ReStatusChip vocab="mortgage" status={a.stage} size="xs" /> },
              ]}
            />
          </RePanel>
        </>
      )}

      <MortgageForm
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onSaved={(a) => {
          setNewOpen(false);
          if (a?.warnings?.length) notify(`Saved with warnings: ${a.warnings.map(humanize).join(', ')}`, 'warning');
          else notify('Application created');
          if (a?.id) navigate(`/real-estate/mortgage-applications/${a.id}`);
          else board.reload();
        }}
      />
      {snack}
    </RePage>
  );
}
